import React, { useState, useCallback, useRef, useMemo, memo, useEffect } from 'react';
import { Text, Title, View, BlurView, Card } from '@constants/Themed';
import { Alert, Platform, RefreshControl, FlatList, ImageBackground, Pressable, ScrollView, KeyboardAvoidingView, StyleSheet, ActivityIndicator, TextInput, Keyboard, Easing, Linking } from 'react-native';
import styles from '@stylesheet';
import SearchComponent from '@components/Global/SearchComponent/SearchBar';
import * as Animatable from 'react-native-animatable';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import FeedComponent from './Components/FeedComponent';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { debounce, set, uniqueId } from 'lodash';
import apis from '@api/api';
import { FontAwesome, Entypo, FontAwesome6 } from '@expo/vector-icons';
import { handleGetLocation, refreshLocation } from '@hooks/handleGetLocation';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { CLEAR_LOCATION, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { isLandscapeSync, isTablet } from 'react-native-device-info'; // isTablet is redundant if using useDeviceType
const Features = React.lazy(() => import('./Components/Features'));
import FlipCard from 'react-native-flip-card';
import ActiveFeedComponent from './Components/ActiveFeedComponent';
import AnimateMarkup from '@hooks/AnimateMarkup';
import { BottomSheetModal, BottomSheetView } from '@gorhom/bottom-sheet';
import { AI_API_URL } from '@api/constant';
import { SizableText, TextAreaProps, Adapt, Sheet, Spinner } from 'tamagui'; // Added Spinner
import { useDeviceType } from '@hooks/useDeviceType'; // Custom hook
import LottieView from 'lottie-react-native';
// --- React Virtuoso Imports ---
import { Virtuoso, VirtuosoGrid } from 'react-virtuoso';
import FeedHeader from './Components/FeedHeader';
import ErrorBoundary from '@utils/ErrorBoundary';
import { MotiView } from 'moti';
import FormModal from './Components/FormModal';

const FeedWidget = ({ availableWidth }) => {
  const selector = useSelector((state: DefaultRootState) => state.app.location);
  const [searchActive, setSearchActive] = useState(false);
  const animRef = useRef(null); // Unused
  const theme = themeSelector();
  const [distanceValue, setDistanceValue] = useState(1);
  const dispatch = useDispatch();
  const { isTablet: deviceIsTablet } = useDeviceType(); // Use alias to avoid name collision
  const landscape = isLandscapeSync(); // This is a sync function, fine to call directly
  // forceHorizontal state logic simplified and made more explicit
  const [forceHorizontal, setForceHorizontal] = useState(Platform.OS === 'web' ? false : (availableWidth > 600 ? false : true));
  const [columns, setColumns] = useState(() => Math.floor(availableWidth / 600)); // Initial calculation
  const [listHorizontal, setListHorizontal] = useState(Platform.OS === 'web' && availableWidth > 800);
  const [flip, setFlip] = useState(false);
  const [activeFeedItem, setActiveFeedItem] = useState(null);
  const [aiResponse, setAIResponse] = useState<string | null>(null);
  const [availWidth, setAvailWidth] = useState(0); // Renamed from componentWidth for clarity
  const mobBottomSheetRef = useRef<BottomSheetModal>(null);
  const [isWidthChanging, setIsWidthChanging] = useState(false); // State to manage width transition
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);


  // Memoize fetchBusinessFeed to ensure stability for useInfiniteQuery
  const fetchBusinessFeed = useCallback(
    async ({ pageParam = 0 }) => {
      const location = await handleGetLocation(dispatch, selector);
      if (location?.coords) {
        try {
          const data = await apis.getFeed(distanceValue, location.coords.latitude, location.coords.longitude, pageParam);
          if (!data || !data.length || !data[0]?.data) { // Check for data[0].data
            return { data: [], nextPage: null };
          }

          const totalItems = data[0].metadata?.[0]?.total;
          const itemsPerPage = 30; // Assuming 30 items per page as per original logic
          let nextPage = null;
          if (typeof totalItems === 'number' && totalItems > 0) {
            const totalPages = Math.ceil(totalItems / itemsPerPage);
            // nextPage should be pageParam + 1, unless it's the last page
            if (pageParam < totalPages - 1) {
              nextPage = pageParam + 1;
            }
          }
          return { data: data[0].data, nextPage };
        } catch (apiError) {
          console.error("API call to getFeed failed:", apiError);
          // Optionally show an alert to the user or set an error state
          Alert.alert('Error', 'Failed to fetch business feed. Please try again.');
          return { data: [], nextPage: null };
        }
      } else {
        dispatch({ type: CLEAR_LOCATION });
        Alert.alert('Error', 'Could not retrieve location data. Please check your location settings and try again.');
        return { data: [], nextPage: null };
      }
    },
    [distanceValue, dispatch, selector]
  );

  // useInfiniteQuery hook for fetching business feed
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
    // error, status // error and status from useInfiniteQuery are not directly used in render, but useful for debugging
    refetch,
  } = useInfiniteQuery({
    queryKey: ['businessFeed', distanceValue],
    queryFn: fetchBusinessFeed,
    getNextPageParam: (lastPage) => lastPage?.nextPage, // Simplified to directly use nextPage
    initialPageParam: 0,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2,
  });

  // Combine pages data into a single flat array
  const flatFeedData = useMemo(() => {
    const flatData = data?.pages?.flatMap((page) => page.data) || [];

    // Inject sample video post in dev mode only
    if (__DEV__) {
      const sampleVideoPost = {
        "_id": "sample-video-post-dev",
        "type": "external_video",
        "external_video": {
          "provider": "youtube",
          "videoId": "dQw4w9WgXcQ",
          "url": "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
          "thumbnail": "https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg",
          "title": "Sample Video Post (Dev Only)"
        },
        "url": "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
        "content": "🎥 Check out this sample video post! This is a development-only sample to test video functionality in the feed.",
        "images": [],
        "postedBy": {
          "_id": "sample-user-dev",
          "profile_information": {
            "name": "Dev",
            "surname": "Team",
            "avatar": {
              "img": "https://storage.googleapis.com/smartsaas-avatar-store/Asset%202.png"
            }
          }
        },
        "likes": [],
        "shares": [],
        "comments": [],
        "likesCount": 0,
        "sharesCount": 0,
        "createdAt": new Date().toISOString(),
        "dist": {
          "calculated": 0
        }
      };

      // Insert sample video post at the beginning
      return [sampleVideoPost, ...flatData];
    }

    return flatData;
  }, [data?.pages]);

  // Handle layout changes for responsive column calculation
  const handleLayout = useCallback((event) => {
    const { width } = event.nativeEvent.layout;
    if (width > 0 && width !== availWidth) {
      setIsWidthChanging(true); // Hide component while width is changing
      setAvailWidth(width);

      // Use a more robust way to detect layout stability or a fixed timeout
      // to re-render after width changes.
      // setTimeout is a hacky way to handle layout changes, consider using
      // a debounced effect if you need to react to stable width.
      setTimeout(() => {
        setIsWidthChanging(false); // Show component again after width change is complete
      }, 300); // Shorter, more responsive timeout
    }
  }, [availWidth]);

  // Effect to calculate columns based on available width and list orientation
  useEffect(() => {
    if (availWidth > 0) {
      const minItemWidth = 300; // Minimum width for a feed item
      // If horizontal, always 1 column. Otherwise, calculate based on available width.
      const newColumns = listHorizontal ? 1 : Math.max(1, Math.floor(availWidth / minItemWidth));
      if (newColumns !== columns) {
        setColumns(newColumns);
      }
    }
  }, [availWidth, listHorizontal, columns]);

  // Toggle list view orientation
  const changeListView = useCallback(() => {
    setListHorizontal(prev => !prev);
    // When switching to horizontal, force columns to 1.
    // When switching to vertical, let useEffect calculate based on width.
    if (!listHorizontal) { // If it was vertical, and now becoming horizontal
      setColumns(1);
    }
  }, [listHorizontal]);

  // Fetch trending data
  const { data: trendData, isLoading: trendLoading } = useQuery({
    queryKey: ['trends'],
    queryFn: async () => {
      const response = await apis.getTrending();
      return response?.storySummaries?.trendingStories || []; // Ensure it returns an array
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Fetch external ads
  const { data: externalAds, isLoading: adsLoading, error: adsError } = useQuery({
    queryKey: ['externalAds', distanceValue],
    queryFn: async () => {
      try {
        const location = await handleGetLocation(dispatch, selector);

        if (location?.coords) {
          const ads = await apis.getExternalAds(
            5, // Request 5 ads
            location.coords.latitude.toString(),
            location.coords.longitude.toString(),
            distanceValue
          );

          return ads || [];
        }
        return [];
      } catch (error) {
        console.error('Failed to fetch external ads:', error);
        return [];
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 15, // 15 minutes - ads refresh less frequently
    retry: 1,
    enabled: true, // Enable ads fetching
  });

  // Inject external ads into feed data at intervals (every 5 posts)
  const flatFeedDataWithAds = useMemo(() => {
    const feed = flatFeedData;
    const ads = externalAds || [];

    // Don't inject ads if feed is empty or loading
    if (feed.length === 0 || ads.length === 0) {
      return feed;
    }

    const result = [];
    let adIndex = 0;
    const AD_INTERVAL = 5; // Insert ad after every 5 posts

    for (let i = 0; i < feed.length; i++) {
      result.push(feed[i]);

      // Insert ad after every AD_INTERVAL posts, but not at the very beginning
      if ((i + 1) % AD_INTERVAL === 0 && adIndex < ads.length && i > 0) {
        result.push(ads[adIndex]);
        adIndex++;
      }
    }

    return result;
  }, [flatFeedData, externalAds]);

  // Debounced scroll handler for search activation
  const debouncedScrollHandler = useMemo(() => debounce(() => {
    if (!searchActive) { // Only set to true once
      setSearchActive(true);
    }
  }, 300), [searchActive]);

  // AI comments fetching logic
  const _getAIComments = useCallback(async () => {
    if (!activeFeedItem || !activeFeedItem.comments || !activeFeedItem.content) {
      setAIResponse("No comments or content available for AI analysis.");
      return;
    }

    setAIResponse("Fetching sentiment...");
    try {
      const commentsForAI = activeFeedItem.comments.flatMap(comment => {
        // Ensure comments are in a format the AI API expects.
        // Assuming the API expects an array of comment objects without the 'user' field.
        const newComment = { ...comment };
        delete newComment.user; // Remove sensitive or unnecessary user info
        return newComment;
      });

      const payload = {
        comments: commentsForAI,
        postContent: activeFeedItem.content
      };

      const response = await fetch(`${AI_API_URL}/analyse-comments`, {
        credentials: __DEV__ ? 'omit' : 'same-origin',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text(); // Get raw error text
        throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
      }

      const data = await response.json(); // response.json() already parses JSON

      // Assuming the AI API returns a JSON object with a 'raw' field containing the sentiment string
      if (data && typeof data.raw === 'string') {
        setAIResponse(data.raw); // Use the raw string directly
      } else {
        setAIResponse("AI response format unexpected.");
        console.warn("AI response format unexpected:", data);
      }

    } catch (error) {
      console.error('Error fetching AI comments:', error);
      setAIResponse(`Error fetching sentiment: ${error.message || error}`);
    }
  }, [activeFeedItem]); // Depend on activeFeedItem

  // Track ad impression when ad is rendered
  const trackAdImpressionIfNeeded = useCallback((item) => {
    if (item?.isExternalAd && item?._id) {
      // Track impression asynchronously to avoid blocking render
      apis.trackAdImpression(
        item._id,
        item.adNetwork,
        item.adCampaignId,
        item.adCreativeId
      ).catch(err => {
        console.error('Failed to track ad impression:', err);
      });
    }
  }, []);

  // Handle ad click with tracking
  const handleAdClick = useCallback(async (item) => {
    if (item?.isExternalAd && item?.url) {
      try {
        // Track click before navigation
        await apis.trackAdClick(
          item._id,
          item.url,
          item.adNetwork,
          item.adCampaignId,
          item.adCreativeId
        );

        // Open ad URL in new tab
        if (Platform.OS === 'web') {
          window.open(item.url, '_blank', 'noopener,noreferrer');
        } else {
          Linking.openURL(item.url);
        }
      } catch (error) {
        console.error('Failed to track ad click:', error);
        // Still open URL even if tracking fails
        if (Platform.OS === 'web') {
          window.open(item.url, '_blank', 'noopener,noreferrer');
        } else {
          Linking.openURL(item.url);
        }
      }
    } else {
      // Regular feed item behavior
      setActiveFeedItem(item);
      if (availWidth > 800) {
        setFlip(true);
      } else {
        mobBottomSheetRef.current?.present();
      }
    }
  }, [availWidth]);

  // Render function for Virtuoso items
  const renderVirtuosoItem = useCallback((index, item) => {
    // Track impression for external ads when rendered (except Google AdSense which handles its own tracking)
    if (item?.isExternalAd && item?.adNetwork !== 'google_adsense') {
      trackAdImpressionIfNeeded(item);
    }

    // Google AdSense ads handle their own clicks, so don't wrap in Pressable
    const isGoogleAdSense = item?.isExternalAd && item?.adNetwork === 'google_adsense';

    return (
      <ErrorBoundary>
        {isGoogleAdSense ? (
          <View
            style={{
              backgroundColor: "transparent",
              margin: 0,
              width: "100%"
            }}
          >
            <FeedComponent
              refresh={refetch}
              item={item}
              index={index}
              type={undefined}
              forceHorizontal={forceHorizontal}
              onDataFormClick={(formId) => {
                setSelectedFormId(formId);
                setShowFormModal(true);
              }}
            />
          </View>
        ) : (
            <Pressable
              style={{
                backgroundColor: "transparent",
                margin: 0,
                shadowRadius: 20,
                shadowOpacity: activeFeedItem?._id === item?._id ? 1 : 0,
                shadowColor: "rgb(90,192,234)",
                // For grid view, ensure items take up correct width
                width: "100%"
              }}
              onPress={() => handleAdClick(item)}
            >
              <FeedComponent
                refresh={refetch}
                item={item}
                index={index}
                type={undefined} // Redundant if undefined is default
                forceHorizontal={forceHorizontal}
                onDataFormClick={(formId) => {
                  setSelectedFormId(formId);
                  setShowFormModal(true);
                }}
              />
            </Pressable>
        )}
      </ErrorBoundary>
    );
  }, [activeFeedItem, availWidth, columns, listHorizontal, forceHorizontal, refetch, handleAdClick, trackAdImpressionIfNeeded]);

   const SponsoredItem = useMemo(() => (
    <FeedComponent
      refresh={refetch}
      item={{
        "_id": "6734365d18a9991523b0fff7",
        "type": "sponsored",
        "businessId": null,
        "company": {
          "company_name": "SmartSaaS",
        },
        "url": "https://smartsaas.pro/",
        "tags": [],
        "postedBy": {
          "_id": "65218f97b125bb2343fc8650",
          "profile_information": {
            "name": "SmartSaaS",
            "surname": "",
            "avatar": {
              "img": ""
            },
          }
        },
        "content": "SmartSaaS is revolutionizing the way businesses operate in the digital age. Join us in building the future of SaaS solutions.",
        "media": null,
        "images": [
          "https://storage.googleapis.com/smartsaas-company-logo-store/662f542a36b10d08e50fd222-logo.png"
        ],
        "likes": [],
        "shares": [],
        "createdAt": "2024-11-13T05:17:17.699Z",
        "dist": {
          "calculated": 38.32240656580821
        },
        "comments": [
          {
            "replies": []
          }
        ],
        "likesCount": 0,
        "sharesCount": 0
      }}
      index={-1}
      forceHorizontal={forceHorizontal}
    />
  ), [forceHorizontal, refetch]);

  const VirtuosoHeaderComponent = useCallback(() => (
    <View style={{ marginBottom: 20, maxWidth:"96%", alignSelf: "center" }}>
      {SponsoredItem}
    </View>
  ), [SponsoredItem]);

  const VirtuosoFooterComponent = useCallback(() => {
    if (isFetching && hasNextPage) {
      return (
        <View style={{ paddingVertical: 20, alignItems: 'center', justifyContent: 'center' }}>
          <Spinner size="small" color="rgb(90,192,234)" />
          <SizableText>Loading more...</SizableText>
        </View>
      );
    }
    return null;
  }, [isFetching, hasNextPage]);

  const VirtuosoEmptyComponent = useCallback(() => (
    <View style={[styles.centerEverything, { flex: 1, justifyContent: 'center', backgroundColor: 'transparent', gap: 20 }]}>
      {Platform.OS !== 'android' && (
        <LottieView source={require('@assets/animations/magnifExample.json')}
          style={{ flex: 1, maxHeight: 250, alignSelf: 'center' }}
          autoPlay
          loop
        />
      )}
      <Title style={{ textAlign: 'center' }}>Nothing to see here!</Title>
      <Text style={{ textAlign: 'center', paddingHorizontal: 20 }}>
        No feed found within your range, try increasing it to see local activity or be the first to make a post in your vicinity
      </Text>
    </View>
  ), []);

  return (
    <View
      onLayout={handleLayout}
      style={{
        left: Platform.select({ ios: 0, default: undefined }),
        gap: forceHorizontal ? 0 : 50,
        top: Platform.select({ default: undefined, web: 0, android: 0 }),
        marginHorizontal: Platform.select({ default: "5%", android: "5%" }),
        marginRight: 0,
        flexDirection: landscape ? "row" : (Platform.OS === 'web' ? "row" : "column"),
        flex: 1,
        alignSelf: "stretch",
        minWidth: Platform.select({ ios: "90%", android: "80%" }),
        backgroundColor: "transparent",
        overflow: "visible"
      }}
    >
      <View style={{
        flex: 4, minWidth: Platform.select({ web: 400 }), overflow: "visible", flexDirection: "column", top: Platform.OS === 'web' ? 0 : deviceIsTablet ? 15 : -11, borderRightWidth: 1, borderLeftWidth: 1, borderColor: '#e0e0e060', minHeight: forceHorizontal ? "60%" : '100%'
      }}>
        <FeedHeader searchActive={searchActive} refetch={refetch} setSearchActive={setSearchActive} distanceCallback={setDistanceValue} />
        <Animatable.View transition={'paddingTop'} style={{ zIndex: -1, flex: 10, marginTop: -70, backgroundColor: 'transparent', paddingBottom: Platform.OS === 'web' || deviceIsTablet ? 0 : 0, alignSelf: "stretch" }}>
          {isLoading || isFetching ? (
            <View style={[styles.centerEverything, { marginTop: "20%", left: "3%", minWidth: "50%", backgroundColor: "transparent" }]}>
              <LottieView source={require('@assets/animations/spin-leaf-loader.json')} autoPlay loop style={{ marginTop: 100, minHeight: 200, flex: 1, alignSelf: 'stretch', maxWidth: '100%', maxHeight: 150, justifyContent: 'center' }} />
              <SizableText>Loading feed...</SizableText>
            </View>
          ) : (
            <View
                style={{ flex: 1, paddingTop: 40, paddingLeft: Platform.select({ web: 20 }), backgroundColor: 'transparent', alignSelf: 'stretch', flexDirection: 'column', justifyContent: 'center' }}
            >
              {
              listHorizontal ? (
                <Virtuoso
                className='hide-scrollbar-virtuoso'
                style={{ flex: 1, height: '100%' }} // Ensure Virtuoso takes full height
                      data={flatFeedDataWithAds}
                itemContent={(index, item) => (
                  <View style={{ marginRight: 20 }}>
                  {renderVirtuosoItem(index, item)}
                  </View>
                )}
                      layout="horizontal"
                      data-hide-scrollbar="true"
                components={{
                  Header: VirtuosoHeaderComponent,
                  Footer: VirtuosoFooterComponent,
                  EmptyPlaceholder: VirtuosoEmptyComponent,
                }}
                endReached={fetchNextPage}
                overscan={200}
                />
              ) : (
                <VirtuosoGrid
                className='hide-scrollbar-virtuoso'
                style={{ flex: 1, height: '100%' }} // Ensure VirtuosoGrid takes full height
                        data={flatFeedDataWithAds}
                itemContent={(index, item) => (
                  <View style={{ margin: 10 }}>
                  {renderVirtuosoItem(index, item)}
                  </View>
                )}
                listClassName={localStyles.gridList}
                components={{
                  Header: VirtuosoHeaderComponent,
                  Footer: VirtuosoFooterComponent,
                  EmptyPlaceholder: VirtuosoEmptyComponent,
                }}
                endReached={fetchNextPage}
                overscan={200}
                />
              )}
            </View>
          )}
        </Animatable.View>
      </View>
      {(Platform.OS === 'web' || isTablet) && availWidth > 800 && (
        <View style={{ flex: 3, width: availWidth * .4, overflow: 'hidden' }} id={"social-feed"}>
          {/* Container to hold the two sliding views and mimic the original flex property */}
          <View style={{ flex: 4 }}>
            {/* --- Front Face --- 
                This view will slide out to the left when 'flip' is true.
            */}
            <MotiView
              style={[styles.face, { paddingVertical: 20, backgroundColor: "transparent", top: 10, alignSelf: "stretch", gap: 20, position: 'absolute', width: '100%', height: '100%' }]}
              animate={{ translateX: flip ? -(availWidth * .4) : 0 }}
              transition={{
                type: 'timing',
                duration: 400,
                easing: Easing.out(Easing.cubic) // Creates a smooth ease-out effect
              }}
            >
              {/* <Card>
                <TutorialVideoListWeb videoList={videos} />
              </Card> */}
              <Card flex={1}>
                <View style={{ flex: 2, borderRadius: 20 }}>
                  <Features />
                </View>
              </Card>
            </MotiView>

            {/* --- Back Face --- 
                This view starts off-screen to the right and slides in to cover the front face.
            */}
            <MotiView
              style={[styles.back, {pointerEvents: flip ? undefined : "none", minWidth: "100%", flexGrow: 10, backgroundColor: "transparent", padding: 20, alignSelf: "stretch", position: 'absolute', width: '100%', height: '100%' }]}
              from={{
                translateX: availWidth * .4,
                opacity: 0 // Start completely transparent and off-screen
              }}
              animate={{
                translateX: flip ? 0 : availWidth * .4, // Animate position: slide in or out
                opacity: flip ? 1 : 0 // Animate opacity: fade in or out
              }}
              transition={{
                type: 'timing',
                duration: 400,
                easing: Easing.out(Easing.cubic)
              }}
              onDidAnimate={(property, finished) => {
                // This logic remains the same, it runs when the slide-out animation is complete
                if (property === 'translateX' && finished && !flip) {
                  setActiveFeedItem(null);
                }
              }}
            >
              {
                activeFeedItem &&
              <Animatable.View animation={'fadeIn'} style={{ flex: 1, alignSelf: "stretch", minHeight: 70, marginBottom: 20, maxHeight: 70 }}>
                <SearchComponent otherComponents={[
                  { icon: <FontAwesome name="arrow-left" size={24} color="black" />, functionName: 'showFilter', function: () => { setFlip(false); }, isIcon: true },
                  { functionName: "AICOMMENTS", icon: <Pressable onPress={_getAIComments} style={{ flex: 1 }}><LottieView style={{ flex: 1, maxHeight: 40, maxWidth: 40 }} source={require("@assets/animations/automation_robot.json")} autoPlay loop /></Pressable>, function: () => { console.log('test'); } },
                  { icon: <FontAwesome name="sort-amount-desc" size={24} color="black" />, functionName: 'showSort', function: () => { }, isIcon: true },
                ]} showSearch />
              </Animatable.View>
              }
              {
                aiResponse &&
                <ScrollView contentContainerStyle={{ paddingHorizontal: 40 }} style={{ borderRadius: 20, padding: 10, borderColor: "rgb(90,192,234)", flex: 1, alignSelf: "stretch", minHeight: 300, marginBottom: 20 }}>
                  <Text style={{ flex: 1 }}>
                    <AnimateMarkup speed={50} value={aiResponse} />
                  </Text>
                </ScrollView>
              }
              <View style={{ flex: 6 }}>
                <ActiveFeedComponent key={activeFeedItem?._id} item={activeFeedItem} index={0} type={undefined} forceHorizontal={forceHorizontal} refresh=
                  {undefined} />
              </View>
            </MotiView>
          </View>
        </View>
      )}
      {
        availWidth < 800 &&
        <BottomSheetModal
          ref={mobBottomSheetRef}
          snapPoints={['70%']}
          onDismiss={() => { setActiveFeedItem(null); setAIResponse(null); }}
          handleComponent={() => null}
          stackBehavior='replace'
          style={{ marginLeft: Platform.select({ web: "30%" }), maxWidth: Platform.select({ web: "60%" }) }}
        >
          <BottomSheetView>
            <ActiveFeedComponent key={activeFeedItem?._id} item={activeFeedItem} index={0} type={undefined} forceHorizontal={forceHorizontal} refresh={refetch} />
          </BottomSheetView>
        </BottomSheetModal>
      }

      {/* Form Modal */}
      <FormModal
        visible={showFormModal}
        formId={selectedFormId}
        onClose={() => {
          setShowFormModal(false);
          setSelectedFormId(null);
        }}
        onSuccess={() => {
          // Optionally refresh the feed after successful form submission
          refetch?.();
        }}
      />
    </View>
  );
};

// --- Local Styles ---
const localStyles = StyleSheet.create({
  centerEverything: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  noteContainer: {
    backgroundColor: 'transparent',
  },
  gridItemGap: {
    margin: 5,
  },
  // Styles for VirtuosoGrid
  gridList: {
    display: 'grid',
    // gridTemplateColumns will be set dynamically via JS in useEffect
    gap: 10, // Adjust gap as needed
    paddingHorizontal: 15, // Match contentContainerStyle
    paddingBottom: 150, // Match contentContainerStyle
  },
});

export default FeedWidget;
